import React, { useRef, useState } from 'react';
import SkillDescriptionPopup from './SkillDescriptionPopup'; // Path as needed

// PurchasableSkillCard now only supports double-click to add (no drag)
const LONG_PRESS_MS = 500;

const PurchasableSkillCard = ({ skill, disabled, onSingleClick, cost }) => {
    const [showPopup, setShowPopup] = useState(false);
    const [popupPos, setPopupPos] = useState(null);
    const longPressTimeout = useRef();

    // Handler: right-click (desktop)
    const handleContextMenu = (e) => {
        e.preventDefault();
        setPopupPos({ x: e.clientX, y: e.clientY });
        setShowPopup(true);
    };

    // Handlers: long press (touch/mouse)
    const handlePointerDown = (e) => {
        // Only left-click or touch
        if (e.button && e.button !== 0) return;
        longPressTimeout.current = setTimeout(() => {
            // Popup centered for touch
            if (e.type === 'touchstart') {
                setPopupPos({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
            } else {
                setPopupPos({ x: e.clientX, y: e.clientY });
            }
            setShowPopup(true);
        }, LONG_PRESS_MS);
    };
    const handlePointerUp = () => clearTimeout(longPressTimeout.current);
    const handlePointerLeave = () => clearTimeout(longPressTimeout.current);

    // Dismiss popup
    const closePopup = () => setShowPopup(false);

    // Prevent click from firing while popup is open
    const handleClick = (e) => {
        if (showPopup) {
            e.preventDefault();
            setShowPopup(false);
            return;
        }
        if (!disabled && onSingleClick) {
            onSingleClick(skill);
        }
    };

    return (
        <>
            <div
                className={`draggable-skill${disabled ? ' disabled-skill' : ''}`}
                style={{ opacity: disabled ? 1 : 1 }}
                tabIndex={disabled ? -1 : 0}
                role="button"
                aria-disabled={disabled}
                title={disabled ? "Skill already added" : `Double-click to add ${skill.name}`}
                onClick={handleClick}
                onContextMenu={handleContextMenu}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerLeave}
                onTouchStart={handlePointerDown}
                onTouchEnd={handlePointerUp}
                onTouchCancel={handlePointerLeave}
            >
                {skill.name} ({cost} SP)
            </div>
            {showPopup && (
                <SkillDescriptionPopup
                    skill={{ ...skill, cost }}
                    position={popupPos}
                    onClose={closePopup}
                />
            )}
            {/* Optional: global overlay to close popup by clicking background */}
            {showPopup && (
                <div
                    style={{
                        position: "fixed",
                        top: 0, left: 0, right: 0, bottom: 0,
                        zIndex: 1000,
                        background: "transparent"
                    }}
                    onClick={closePopup}
                />
            )}
        </>
    );
};




const AvailableSkills = ({
                             availableSkills,
                             skillsetMap,
                             activeSkillsetTab,
                             setActiveSkillsetTab,
                             currentCharacter,
                             getSkillCost,
                             handleDropSkill
                         }) => {
    // Group the skills by skillset
    const grouped = availableSkills.reduce((groups, skill) => {
        const groupKey = String(skill.skillset);
        if (!groups[groupKey]) groups[groupKey] = [];
        groups[groupKey].push(skill);
        return groups;
    }, {});

    // Sort skills within each group by name
    Object.values(grouped).forEach(skills =>
        skills.sort((a, b) => a.name.localeCompare(b.name))
    );

    // Sort tabs by skillset name
    const groupKeys = Object.keys(grouped).sort((a, b) => {
        const nameA = (skillsetMap[a] || "").toUpperCase();
        const nameB = (skillsetMap[b] || "").toUpperCase();
        return nameA.localeCompare(nameB);
    });

    return (
        <div className="available-skills-tabbed">
            <div className="tabs-header">
                {groupKeys.map(key => (
                    <button
                        key={key}
                        className={`tab-button${activeSkillsetTab === key ? ' active' : ''}`}
                        onClick={() => setActiveSkillsetTab(key)}
                        type="button"
                    >
                        {skillsetMap[key] || `Skillset ${key}`}
                    </button>
                ))}
            </div>
            <div className="tab-content">
                {activeSkillsetTab && grouped[activeSkillsetTab] ? (
                    grouped[activeSkillsetTab].map(skill => {
                        const isDisabled = currentCharacter.skills?.some(s => s.skillId === skill.skillId);
                        const computedCost = getSkillCost(skill, 1);
                        return (
                            <PurchasableSkillCard
                                key={skill.skillId}
                                skill={skill}
                                cost={computedCost}
                                disabled={isDisabled}
                                onSingleClick={handleDropSkill}
                            />
                        );
                    })
                ) : (
                    <p>No skills available in this category.</p>
                )}
            </div>
        </div>
    );
};

export default AvailableSkills;
